import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import { DashboardCard } from "@dashboard/components/Card";
import Date from "@dashboard/components/Date";
import { ListPageLayout } from "@dashboard/components/Layouts";
import ResponsiveTable from "@dashboard/components/ResponsiveTable";
import TableRowLink from "@dashboard/components/TableRowLink";
import { CustomerFormsTabs } from "@dashboard/customerForms/CustomerFormsTabs";
import {
  FeedbackKindEnum,
  useFeedbackMessagesQuery,
  useFeedbackMessageUpdateMutation,
} from "@dashboard/graphql";
import useDebounce from "@dashboard/hooks/useDebounce";
import useNotifier from "@dashboard/hooks/useNotifier";
import { TableBody, TableCell, TableHead } from "@material-ui/core";
import { Box, Button, Input, Skeleton, Text } from "@saleor/macaw-ui-next";
import { useState } from "react";
import { useIntl } from "react-intl";

import { feedbackMessages as m } from "./messages";

type StatusFilter = "open" | "resolved" | "all";

const numberOfColumns = 5;

const kindLabel = {
  [FeedbackKindEnum.COMPLAINT]: m.kindComplaint,
  [FeedbackKindEnum.OPINION]: m.kindOpinion,
} as const;

export const FeedbackList = () => {
  const intl = useIntl();
  const notify = useNotifier();
  const [status, setStatus] = useState<StatusFilter>("open");
  const [kind, setKind] = useState<FeedbackKindEnum | null>(null);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const debouncedSetSearch = useDebounce((value: string) => setSearch(value.trim()), 300);

  const { data, loading, refetch } = useFeedbackMessagesQuery({
    fetchPolicy: "cache-and-network",
    variables: {
      kind,
      isResolved: status === "all" ? null : status === "resolved",
      search: search || null,
    },
  });
  const [updateMessage, updateResult] = useFeedbackMessageUpdateMutation();
  const messages = data?.feedbackMessages;

  const handleToggle = async (id: string, isResolved: boolean) => {
    const result = await updateMessage({ variables: { id, isResolved } });
    const errors = result.data?.feedbackMessageUpdate?.errors ?? [];

    if (result.errors?.length || errors.length) {
      notify({ status: "error", text: intl.formatMessage(m.updateFailed) });

      return;
    }

    await refetch();
  };

  const statusFilters: Array<[StatusFilter, typeof m.filterAll]> = [
    ["open", m.filterOpen],
    ["resolved", m.filterResolved],
    ["all", m.filterAll],
  ];
  const kindFilters: Array<[FeedbackKindEnum | null, typeof m.kindAll]> = [
    [null, m.kindAll],
    [FeedbackKindEnum.COMPLAINT, m.kindComplaint],
    [FeedbackKindEnum.OPINION, m.kindOpinion],
  ];

  return (
    <ListPageLayout>
      <TopNav title={intl.formatMessage(m.title)} withoutBorder isAlignToRight={false} />
      <Box display="flex" flexDirection="column">
        <CustomerFormsTabs active="feedback" />
        <DashboardCard>
          <DashboardCard.Header>
            <DashboardCard.Subtitle>{intl.formatMessage(m.subtitle)}</DashboardCard.Subtitle>
          </DashboardCard.Header>
          <DashboardCard.Content>
            <Box display="flex" flexWrap="wrap" gap={4} alignItems="center" paddingBottom={4}>
              <Box display="flex" gap={1}>
                {statusFilters.map(([value, label]) => (
                  <Button
                    key={value}
                    size="small"
                    variant={status === value ? "primary" : "secondary"}
                    onClick={() => setStatus(value)}
                  >
                    {intl.formatMessage(label)}
                  </Button>
                ))}
              </Box>
              <Box display="flex" gap={1}>
                {kindFilters.map(([value, label]) => (
                  <Button
                    key={value ?? "all"}
                    size="small"
                    variant={kind === value ? "primary" : "secondary"}
                    onClick={() => setKind(value)}
                  >
                    {intl.formatMessage(label)}
                  </Button>
                ))}
              </Box>
              <Box flexGrow="1" __minWidth="220px">
                <Input
                  size="small"
                  value={searchInput}
                  maxLength={100}
                  placeholder={intl.formatMessage(m.searchPlaceholder)}
                  onChange={event => {
                    setSearchInput(event.target.value);
                    debouncedSetSearch(event.target.value);
                  }}
                />
              </Box>
            </Box>
          </DashboardCard.Content>
          <ResponsiveTable data-test-id="feedback-list">
            <TableHead>
              <TableRowLink>
                <TableCell>{intl.formatMessage(m.colDate)}</TableCell>
                <TableCell>{intl.formatMessage(m.colKind)}</TableCell>
                <TableCell>{intl.formatMessage(m.colFrom)}</TableCell>
                <TableCell>{intl.formatMessage(m.colMessage)}</TableCell>
                <TableCell>{intl.formatMessage(m.colStatus)}</TableCell>
              </TableRowLink>
            </TableHead>
            <TableBody>
              {messages === undefined && loading ? (
                <TableRowLink>
                  <TableCell colSpan={numberOfColumns}>
                    <Skeleton />
                  </TableCell>
                </TableRowLink>
              ) : !messages?.length ? (
                <TableRowLink data-test-id="empty-list-message">
                  <TableCell colSpan={numberOfColumns}>{intl.formatMessage(m.empty)}</TableCell>
                </TableRowLink>
              ) : (
                messages.map(item => (
                  <TableRowLink key={item.id} data-test-id={"feedback-" + item.id}>
                    <TableCell style={{ verticalAlign: "top", whiteSpace: "nowrap" }}>
                      <Date date={item.createdAt} />
                    </TableCell>
                    <TableCell style={{ verticalAlign: "top" }}>
                      <Text
                        size={2}
                        fontWeight="medium"
                        color={item.kind === FeedbackKindEnum.COMPLAINT ? "critical1" : "default1"}
                      >
                        {intl.formatMessage(kindLabel[item.kind])}
                      </Text>
                    </TableCell>
                    <TableCell style={{ verticalAlign: "top" }}>
                      <Text display="block">{item.name}</Text>
                      <Text size={2} color="default2">
                        <a href={`mailto:${item.email}`}>{item.email}</a>
                      </Text>
                    </TableCell>
                    <TableCell style={{ verticalAlign: "top" }}>
                      <Text style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                        {item.message}
                      </Text>
                    </TableCell>
                    <TableCell style={{ verticalAlign: "top" }}>
                      <Button
                        size="small"
                        variant="secondary"
                        disabled={updateResult.loading}
                        onClick={() => handleToggle(item.id, !item.isResolved)}
                      >
                        {intl.formatMessage(item.isResolved ? m.markOpen : m.markResolved)}
                      </Button>
                    </TableCell>
                  </TableRowLink>
                ))
              )}
            </TableBody>
          </ResponsiveTable>
        </DashboardCard>
      </Box>
    </ListPageLayout>
  );
};
